import config from '@/config.ts';
import { FABRIC_HEADERS } from '@/utilities/constants/metadata.ts';
import { useStore } from '@/store/useStore';
import { setUserSessionContext } from '@/store/actions';
import {
  buildFabricAuthHeaders,
  createApiAxios,
  getFabricAuthTokens,
} from '@/utilities/axios/api-axios';
import { isAxiosError } from 'axios';

interface ServerTimeResponse {
  datetime?: string;
  timezone?: string;
  error?: { code: string; message: string } | null;
}

export type ServerTimeFetchResult = {
  /** Epoch ms — server datetime when synced, otherwise client fallback. */
  timeMs: number;
  /** `true` only when aggregation returned a valid `datetime`. */
  fromServer: boolean;
};

/** First minute of consecutive failures: at most one HTTP request per second. */
const FAST_RETRY_INTERVAL_MS = 1_000;
/** After the first minute of failures: at most one HTTP request per five seconds. */
const SLOW_RETRY_INTERVAL_MS = 5_000;
const FAST_PHASE_DURATION_MS = 60_000;

let failurePhaseStartedAt: number | null = null;
let lastHttpAttemptAt = 0;
let serverTimeSynced = false;

export const isServerTimeFetchEnabled = (): boolean =>
  Boolean(config?.fabric?.endpoints?.aggregation?.serverTime);

export const isServerTimeSynced = (): boolean => serverTimeSynced;

const getMinRetryIntervalMs = (now: number): number => {
  if (failurePhaseStartedAt === null) {
    return 0;
  }

  const elapsed = now - failurePhaseStartedAt;
  return elapsed < FAST_PHASE_DURATION_MS
    ? FAST_RETRY_INTERVAL_MS
    : SLOW_RETRY_INTERVAL_MS;
};

/** Delay before the next sync attempt while aggregation is still failing. */
export const getServerTimeRetryDelayMs = (): number => {
  const now = Date.now();
  if (failurePhaseStartedAt === null) {
    return FAST_RETRY_INTERVAL_MS;
  }

  const minInterval = getMinRetryIntervalMs(now);
  const elapsedSinceLastAttempt = now - lastHttpAttemptAt;
  return Math.max(0, minInterval - elapsedSinceLastAttempt);
};

const shouldThrottleHttp = (now: number): boolean => {
  if (failurePhaseStartedAt === null) {
    return false;
  }

  return now - lastHttpAttemptAt < getMinRetryIntervalMs(now);
};

const recordHttpAttempt = (now: number): void => {
  lastHttpAttemptAt = now;
};

const recordHttpFailure = (now: number): void => {
  if (failurePhaseStartedAt === null) {
    failurePhaseStartedAt = now;
  }
  serverTimeSynced = false;
  lastHttpAttemptAt = now;
};

const recordHttpSuccess = (): void => {
  failurePhaseStartedAt = null;
  lastHttpAttemptAt = 0;
  serverTimeSynced = true;
};

/** @internal test helper */
export const resetServerTimeFetchBackoffForTests = (): void => {
  failurePhaseStartedAt = null;
  lastHttpAttemptAt = 0;
  serverTimeSynced = false;
};

const clientFallback = (now: number): ServerTimeFetchResult => ({
  timeMs: now,
  fromServer: false,
});

/**
 * Fetch current server time. HTTP is rate-limited after failures (1/s for the
 * first minute, then 1/5s) but callers should keep scheduling retries until
 * `fromServer` is true so a recovered service is picked up.
 */
export const fetchServerTime = async (): Promise<ServerTimeFetchResult> => {
  const now = Date.now();

  if (!isServerTimeFetchEnabled()) {
    return clientFallback(now);
  }

  if (shouldThrottleHttp(now)) {
    return clientFallback(now);
  }

  recordHttpAttempt(now);

  const { baseURL, region, brand } = useStore.getState().runtime;
  const endpoint = config.fabric.endpoints.aggregation.serverTime as string;
  const timezone =
    Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || 'UTC';

  let fabricAuthHeaders: Record<string, string>;
  try {
    fabricAuthHeaders = buildFabricAuthHeaders(
      await getFabricAuthTokens('FETCH_SERVER_TIME'),
    );
  } catch {
    return clientFallback(now);
  }

  try {
    const instance = createApiAxios();
    const response = await instance.get(endpoint, {
      baseURL,
      headers: {
        ...fabricAuthHeaders,
        [FABRIC_HEADERS.X_BRAND]: brand,
        [FABRIC_HEADERS.X_REGION_CODE]: region,
      },
      params: { _: now, timezone },
    });

    const data = response?.data as ServerTimeResponse;

    if (data?.timezone) {
      setUserSessionContext({ timezone: data.timezone });
    }

    const datetimeStr = data?.datetime;
    if (datetimeStr) {
      const ms = new Date(datetimeStr).valueOf();
      if (!Number.isNaN(ms)) {
        recordHttpSuccess();
        return { timeMs: ms, fromServer: true };
      }
    }

    recordHttpFailure(Date.now());
    return clientFallback(Date.now());
  } catch (error) {
    recordHttpFailure(Date.now());

    if (isAxiosError(error) && error.response?.status === 503) {
      return clientFallback(Date.now());
    }

    return clientFallback(Date.now());
  }
};

export default fetchServerTime;
